import { useAuthenticatedApi } from "@webng/react-app-common";
import { ApiClient } from "@webng/tickaroo-api";
import { QuizSubmitRequest, QuizSubmitResponse } from "@webng-types/interactive-backend";
import { useCallback } from "react";
import {
  MutationFunction,
  UseMutateFunction,
  useMutation,
} from "@tanstack/react-query";

interface UseQuizSubmitProps {
  organizationId: string | undefined;
  quizId: string | undefined;
  onSuccess?: (data: QuizSubmitResponse) => void;
  onError?: (error: Error) => void;
}

export function useQuizSubmit({
  organizationId,
  quizId,
  onSuccess,
  onError,
}: UseQuizSubmitProps): [boolean, UseMutateFunction<QuizSubmitResponse, Error, QuizSubmitRequest>, QuizSubmitResponse | undefined, boolean] {
  const apiClient = useAuthenticatedApi();

  const mutationFn: MutationFunction<QuizSubmitResponse, QuizSubmitRequest> = useCallback(
    async (submitData: QuizSubmitRequest) => {
      if (!organizationId || !quizId) {
        throw new Error("organizationId and quizId are required");
      }
      return apiClient.post<QuizSubmitResponse>(
        ApiClient.API_INTERACTIVE_V1_QUIZ_SUBMIT,
        submitData,
        { organizationId, quizId }
      );
    },
    [apiClient, organizationId, quizId]
  );

  const { mutate, isError, data, isPending } = useMutation<
    QuizSubmitResponse,
    Error,
    QuizSubmitRequest
  >({
    mutationFn,
    retry: 0,
    onSuccess,
    onError,
  });

  return [isError, mutate, data, isPending];
}
